import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Github, Twitter, Facebook, ArrowUpRight } from 'lucide-react';
import { gsap, useGSAP } from '@/lib/gsap-config';
import { Logo } from '@/components/common/Logo';

export function Footer() {
  const { t } = useTranslation();
  const footerRef = useRef<HTMLElement>(null);
  const wireframePathsRef = useRef<(SVGPathElement | null)[]>([]);

  useGSAP(() => {
    gsap.fromTo(
      wireframePathsRef.current.filter(Boolean),
      { drawSVG: '0% 0%' },
      {
        drawSVG: '0% 100%',
        duration: 1.6,
        stagger: 0.1,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: footerRef.current,
          start: 'top 85%',
        },
      }
    );

  }, { scope: footerRef });

  return (
    <footer
      ref={footerRef}
      className="relative bg-card/60 dark:bg-[#06080A] border-t border-border/80 pt-20 pb-12 overflow-hidden select-none"
    >
      {/* Background Architectural Isometric Slide Wireframe (DrawSVG) */}
      <div className="absolute top-0 right-0 w-[500px] h-[350px] pointer-events-none opacity-20 dark:opacity-30 overflow-hidden">
        <svg viewBox="0 0 500 350" fill="none" className="w-full h-full text-foreground/40">
          {/* Isometric Deck 16:9 Wireframe */}
          <path
            ref={(el) => (wireframePathsRef.current[0] = el)}
            d="M 120,40 L 440,80 L 360,260 L 40,220 Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            ref={(el) => (wireframePathsRef.current[1] = el)}
            d="M 150,70 L 410,105 L 340,240 L 80,205 Z"
            stroke="currentColor"
            strokeWidth="1"
            strokeDasharray="4 4"
          />
          <path
            ref={(el) => (wireframePathsRef.current[2] = el)}
            d="M 170,110 L 320,130 M 160,140 L 290,158 M 150,170 L 240,182"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-brand"
          />
          {/* Coordinate Marks */}
          <circle cx="120" cy="40" r="3" className="fill-brand" />
          <circle cx="440" cy="80" r="3" className="fill-brand" />
          <circle cx="360" cy="260" r="3" className="fill-brand" />
          <circle cx="40" cy="220" r="3" className="fill-brand" />
        </svg>
      </div>

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        {/* 4-Column Navigation Layout */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-16">
          {/* Brand Col */}
          <div className="col-span-2 space-y-4">
            <Logo size="lg" />
            <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
              {t('footer.desc')}
            </p>

          </div>

          {/* Product Col */}
          <div>
            <h4 className="text-xs font-mono font-bold uppercase tracking-wider mb-4 text-foreground">
              {t('footer.product')}
            </h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground font-sans">
              <li>
                <a href="/#ideation-lab" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.features')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/#pricing" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.pricing')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.templates')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.changelog')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
            </ul>
          </div>

          {/* Resources Col */}
          <div>
            <h4 className="text-xs font-mono font-bold uppercase tracking-wider mb-4 text-foreground">
              {t('footer.resources')}
            </h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground font-sans">
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.blog')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.help')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.community')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.showcase')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
            </ul>
          </div>

          {/* Company & Legal Col */}
          <div>
            <h4 className="text-xs font-mono font-bold uppercase tracking-wider mb-4 text-foreground">
              {t('footer.company')}
            </h4>
            <ul className="space-y-2.5 text-sm text-muted-foreground font-sans">
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.about')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.careers')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.privacy')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
              <li>
                <a href="/coming-soon" className="hover:text-foreground transition-colors flex items-center gap-1 group">
                  <span>{t('footer.terms')}</span>
                  <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all text-brand" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom copyright & security credentials */}
        <div className="flex flex-col md:flex-row items-center justify-between pt-8 border-t border-border/50 gap-4 text-xs font-mono text-muted-foreground">
          <p>© {new Date().getFullYear()} Lamslide. {t('footer.copyright')}</p>

          <div className="flex items-center gap-6">
            <span className="text-[11px] text-muted-foreground/70">ISO-27001 &amp; AES-256 ENCRYPTED</span>
            <div className="flex items-center gap-3">
              <a href="/coming-soon" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" aria-label="Twitter">
                <Twitter size={15} />
              </a>
              <a href="/coming-soon" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" aria-label="Github">
                <Github size={15} />
              </a>
              <a href="/coming-soon" className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" aria-label="Facebook">
                <Facebook size={15} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;

